import React, { useState } from "react";
import {
  ChevronDown,
  PawPrint,
  Heart,
  Shield,
  Calendar,
  CreditCard,
  BarChart,
} from "lucide-react";

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Are all pet hotels on Stayfur verified?",
      answer:
        "Yes. We only list SSM-registered and certified pet hotels. Every hotel goes through a verification process to ensure professional standards.",
      icon: <Shield className="w-5 h-5" />,
      color: "#B46B42",
      bgColor: "#FDF1E9",
    },
    {
      question: "How do I know my pet will be safe?",
      answer:
        "We work only with licensed professionals who meet safety, hygiene, and care standards. You can also read verified reviews from other pet parents who have booked through Stayfur.",
      icon: <Heart className="w-5 h-5" />,
      color: "#E85A4F",
      bgColor: "#FEF0F0",
    },
    {
      question: "What's the shared room option?",
      answer:
        "Some hotels offer cheaper rates when multiple pets stay in the same room. It's completely optional and only available at participating hotels.",
      icon: <PawPrint className="w-5 h-5" />,
      color: "#8D6E63",
      bgColor: "#F5F0EE",
    },
    {
      question: "Can I cancel or reschedule my booking?",
      answer:
        "Cancellation and rescheduling policies vary by hotel. You'll see the policy clearly listed before you book. We recommend checking it before confirming your stay.",
      icon: <Calendar className="w-5 h-5" />,
      color: "#5C6BC0",
      bgColor: "#EFF0F9",
    },
    {
      question: "Do I pay through Stayfur or directly to the hotel?",
      answer:
        "You pay directly through Stayfur's secure system. We'll handle the rest — and your booking is instantly confirmed.",
      icon: <CreditCard className="w-5 h-5" />,
      color: "#43A047",
      bgColor: "#EEFAF0",
    },
    {
      question: "How are prices calculated?",
      answer:
        "Prices may vary depending on your pet's species, size, room type, and seasonal demand. Some hotels also offer weekend pricing or shared room / multiple pet discounts.",
      icon: <BarChart className="w-5 h-5" />,
      color: "#7E57C2",
      bgColor: "#F3EFFA",
    },
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div
      className="max-w-4xl mx-auto px-4 pb-16 mt-10 relative overflow-hidden"
      style={{ position: "relative" }}
    >

      <div className="text-center mb-10 relative">
        <span className="inline-block relative">
          <PawPrint className="w-8 h-8 text-[#B46B42] absolute -top-5 -right-40 transform rotate-[-15deg] opacity-70" />
        </span>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Frequently Asked Questions
        </h2>
        <p className="text-gray-600">Got questions? We've got answers</p>
        <div className="h-1 w-20 bg-[#B46B42] mx-auto mt-4 rounded-full"></div>
      </div>

      <div className="space-y-4 relative">
        {faqs.map((faq, index) => (
          <div
            key={index}
            className={`rounded-xl overflow-hidden transition-all duration-300 ${openIndex === index
              ? "shadow-md scale-[1.02]"
              : "shadow-sm hover:shadow-md"
              }`}
            style={{
              backgroundColor: openIndex === index ? faq.bgColor : "white",
              borderLeft:
                openIndex === index ? `4px solid ${faq.color}` : "none",
              transform: `perspective(1000px) rotateY(${openIndex === index ? "2deg" : "0deg"
                })`,
              transformOrigin: "center left",
            }}
          >
            <button
              className={`w-full text-left p-4 focus:outline-none transition-colors duration-300`}
              onClick={() => toggleFAQ(index)}
              aria-expanded={openIndex === index}
              aria-controls={`faq-answer-${index}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div
                    className="mr-3 p-2 rounded-full transition-all duration-300"
                    style={{
                      backgroundColor:
                        openIndex === index ? faq.color : faq.bgColor,
                      color: openIndex === index ? "white" : faq.color,
                    }}
                  >
                    {faq.icon}
                  </div>
                  <h3 className="font-medium text-gray-800">{faq.question}</h3>
                </div>
                <div
                  className="ml-4 flex-shrink-0 transition-transform duration-300"
                  style={{
                    transform:
                      openIndex === index ? "rotate(180deg)" : "rotate(0deg)",
                    color: faq.color,
                  }}
                >
                  <ChevronDown className="w-5 h-5" />
                </div>
              </div>
            </button>

            <div
              id={`faq-answer-${index}`}
              className="overflow-hidden transition-all duration-300 ease-out"
              style={{
                maxHeight: openIndex === index ? "200px" : "0",
                opacity: openIndex === index ? 1 : 0,
              }}
            >
              <div className="px-4 pb-4 pl-14">
                <div
                  className="relative p-4 rounded-lg"
                  style={{ backgroundColor: "rgba(255, 255, 255, 0.7)" }}
                >
                  <span
                    className="absolute top-0 left-0 h-full w-1 rounded-full"
                    style={{ backgroundColor: faq.color }}
                  ></span>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Decorative paw prints */}
      <div className="absolute bottom-8 right-8">
        <PawPrint className="w-6 h-6 text-[#B46B42] opacity-20 transform rotate-12" />
      </div>
      <div className="absolute top-20 left-6">
        <PawPrint className="w-5 h-5 text-[#B46B42] opacity-20 transform rotate-30" />
      </div>
    </div>
  );
};

export default FAQSection;
